import { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';

/**
 * CreateProjectPopup - Modal for creating new projects
 *
 * Props:
 * - isOpen: boolean - controls visibility
 * - onConfirm: (title, description) => void - called when project is created
 * - onCancel: () => void - called when popup is closed
 */
const CreateProjectPopup = ({ isOpen, onConfirm, onCancel }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  // Reset form when popup opens
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setError('');
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    // Validate title
    if (!title.trim()) {
      setError('Project title is required');
      return;
    }

    setIsCreating(true);
    setError('');
    try {
      await onConfirm(title.trim(), description.trim());
    } catch (err) {
      setError(err.message || 'Failed to create project');
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && title.trim()) {
      e.preventDefault();
      handleConfirm();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="w-full max-w-md mx-4 rounded-xl backdrop-blur-md p-4 sm:p-5"
        style={{
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          backgroundColor: 'var(--glass-modal-bg)',
          boxShadow: 'var(--shadow-box)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              style={{ color: 'var(--accent-color, #3b82f6)' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
              New Project
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg transition-colors"
            style={{
              color: 'var(--text-secondary)',
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            disabled={isCreating}
          >
            <FaTimes size={14} />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Title Input */}
          <div>
            <label
              htmlFor="project-title"
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--text-primary)' }}
            >
              Title <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="project-title"
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (error) setError('');
              }}
              onKeyDown={handleKeyDown}
              placeholder="Enter project title"
              className="w-full px-3 py-2 rounded-lg text-sm transition-all"
              style={{
                backgroundColor: 'var(--glass-input-bg)',
                color: 'var(--text-primary)',
                border: error ? '1px solid #ef4444' : '1px solid transparent',
                outline: 'none'
              }}
              onFocus={(e) => {
                if (!error) e.currentTarget.style.border = '1px solid var(--accent-color, #3b82f6)';
              }}
              onBlur={(e) => {
                if (!error) e.currentTarget.style.border = '1px solid transparent';
              }}
              autoFocus
              disabled={isCreating}
              maxLength={100}
            />
            {error && (
              <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                {error}
              </p>
            )}
          </div>

          {/* Description Input */}
          <div>
            <label
              htmlFor="project-description"
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--text-primary)' }}
            >
              Description <span style={{ color: 'var(--text-secondary)' }}>(optional)</span>
            </label>
            <textarea
              id="project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this project is about"
              rows={3}
              className="w-full px-3 py-2 rounded-lg text-sm transition-all resize-none"
              style={{
                backgroundColor: 'var(--glass-input-bg)',
                color: 'var(--text-primary)',
                border: '1px solid transparent',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.currentTarget.style.border = '1px solid var(--accent-color, #3b82f6)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.border = '1px solid transparent';
              }}
              disabled={isCreating}
              maxLength={500}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-5">
          <button
            onClick={onCancel}
            className="flex-1 py-2 px-3 rounded-lg transition-all duration-200 text-sm font-medium"
            style={{
              backgroundColor: 'var(--glass-input-bg)',
              color: 'var(--text-primary)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--glass-input-bg)';
            }}
            disabled={isCreating}
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            disabled={isCreating || !title.trim()}
            className="flex-1 py-2 px-3 rounded-lg transition-all duration-200 text-white text-sm font-medium"
            style={{
              backgroundColor: (isCreating || !title.trim()) ? '#6b7280' : 'var(--accent-color, #3b82f6)',
              opacity: (isCreating || !title.trim()) ? 0.6 : 1,
              cursor: (isCreating || !title.trim()) ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!isCreating && title.trim()) {
                e.currentTarget.style.filter = 'brightness(1.1)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.filter = 'brightness(1)';
            }}
          >
            {isCreating ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Creating...
              </div>
            ) : (
              'Create Project'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProjectPopup;
